import { useState, useEffect } from 'react';
import { Box, Grid, Typography, Paper, Divider, Button, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useMediaQuery } from '@mui/system';
import { formatTransactionType } from '../../../utils/formateTransactionType';
import axios from 'axios';

// Utility: Normalize object keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return {};
    return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key.toLowerCase(), val])
    );
};

const Section = ({ title, data, stepIndex, onEditStep, onChangeNumber, changedDocumentNumber }) => {
    // If no data or empty object, only render header
    if (
        data == null ||
        (typeof data === 'object' && Object.keys(data).length === 0)
    ) {
        return (
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">{title}</Typography>
                    <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}>
                        Edit
                    </Button>
                </Box>
            </Paper>
        );
    }

    // Normalize client.details sub-object
    const contact = normalizeKeys(data.details);

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{title}</Typography>
                <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}>
                    Edit
                </Button>
            </Box>

            {title === 'Client Details' && (
                <>
                    <Divider sx={{ my: 2 }} />

                    {data.companyName && (
                        <Grid item xs={12} sm={6} md={5}>
                            <Typography component="span" variant="body2" color="text.secondary">
                                🏨 Company:&nbsp;
                            </Typography>
                            <Typography component="span" variant="body2" fontWeight="500">
                                {data.companyName}
                            </Typography>
                        </Grid>
                    )}
                    <Grid container spacing={1} mt={0.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        Challan No.:&nbsp;
                                    </Typography>
                                    <Typography component="span" variant="body2" fontWeight="500">
                                        {changedDocumentNumber || data.quotationNumber || data.challanNumber || '-'}
                                    </Typography>
                                </Box>
                                {(changedDocumentNumber || data.quotationNumber || data.challanNumber) && (
                                    <Button size="small" variant="outlined" color="primary" onClick={onChangeNumber} sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}>
                                        Change
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                        {data.inquiryDate && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    📅 Date:&nbsp;
                                </Typography>
                                <Typography component="span" variant="body2" fontWeight="500">
                                    {data.inquiryDate}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Grid container spacing={1} mt={0.5}>
                        {contact.contactperson && (
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    👤 Contact:&nbsp;
                                </Typography>
                                <Typography component="span" variant="body2" fontWeight="500">
                                    {contact.contactperson}
                                </Typography>
                            </Grid>
                        )}
                        {contact.mobileno && (
                            <Grid item xs={12} sm={6} md={2}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    📞 {contact.mobileno}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container spacing={1} mt={0.5}>
                        {contact.email && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    ✉️ {contact.email}
                                </Typography>
                            </Grid>
                        )}
                        {(contact.city || contact.state) && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center">
                                    {contact.city && (
                                        <Typography variant="body2" color="text.secondary">
                                            🏙️ {contact.city}
                                        </Typography>
                                    )}
                                    {contact.city && contact.state && (
                                        <Typography sx={{ mx: 0.5 }} variant="body2" color="text.secondary">
                                            ,
                                        </Typography>
                                    )}
                                    {contact.state && (
                                        <Typography variant="body2" color="text.secondary">
                                            {contact.state}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {title === 'Consigner Details' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                        {/* Ship From */}
                        <Box flex={1}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Ship From</Typography>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Client:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipFrom.clientname}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Contact:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipFrom.ContactPerson}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Mobile:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipFrom.mobileNo}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Address:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipFrom.Address}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" flexWrap="wrap">
                                {data.shipFrom.City && <Typography variant="body2" color="text.secondary">🏙️ {data.shipFrom.City}</Typography>}
                                {data.shipFrom.City && data.shipFrom.State && <Typography sx={{ mx: 0.5 }} variant="body2" color="text.secondary">,</Typography>}
                                {data.shipFrom.State && <Typography variant="body2" color="text.secondary">{data.shipFrom.State}</Typography>}
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', md: 'block' } }} />

                        {/* Ship To */}
                        <Box flex={1}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Ship To</Typography>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Client:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipTo.clientname}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Contact:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipTo.ContactPerson}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Mobile:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipTo.mobileNo}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Address:&nbsp;</Typography>
                                <Typography variant="body2" fontWeight="500">{data.shipTo.Address}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" flexWrap="wrap">
                                {data.shipTo.City && <Typography variant="body2" color="text.secondary">🏙️ {data.shipTo.City}</Typography>}
                                {data.shipTo.City && data.shipTo.State && <Typography sx={{ mx: 0.5 }} variant="body2" color="text.secondary">,</Typography>}
                                {data.shipTo.State && <Typography variant="body2" color="text.secondary">{data.shipTo.State}</Typography>}
                            </Box>
                        </Box>
                    </Box>
                </>
            )}

            {title === 'Transporter Details' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={1}>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Transporter:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.transporterName}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Trans Mode:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.transMode}</Typography>
                            </Box>
                        </Grid>


                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>GST No.:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.gstNo}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Vehicle:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.vehicleNumber}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Vehical Type:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.vehicleType}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Total Pkg.:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.totalPkg}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Sub type:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.subType}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Dispatch From:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.dispatchFrom}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Dispatch To:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.dispatchTo}</Typography>
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>LR No.:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.lrNumber}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>LR Date:</Typography>
                                <Typography variant="body2" fontWeight="500">{data.lrDate}</Typography>
                            </Box>
                        </Grid>

                    </Grid>
                </>
            )}

            {title === 'Export Details' && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                        {[
                            { label: 'BL No.', value: data.blNumber },
                            { label: 'BL Date', value: data.blDate },
                            { label: 'Delivery Terms', value: data.deliveryTerms },
                            { label: 'Terms Of Pay', value: data.termsOfPayments },
                            { label: 'LUT Ref. No.', value: data.lutReferenceNumber },
                            { label: 'LUT Ref. Date', value: data.lutReferenceDate },
                            { label: 'Country Origin', value: data.countryOfOrigin },
                            { label: 'Final Dest.', value: data.countryOfFinalDestination },
                            { label: 'Pre-Carriage By', value: data.preCarraigeBy },
                            { label: 'Pre-Carriage Place', value: data.preCarraigePlace },
                            { label: 'Vessel/Flight No.', value: data.vesselOrFlightNumber },
                            { label: 'Port of Landing', value: data.portOfLanding },
                            { label: 'Port of Discharge', value: data.portOfDischarge },
                            { label: 'Final Destination', value: data.finalDestination },
                            { label: 'Carriage By', value: data.carraigeBy },
                            { label: 'Express Duty', value: data.expressDuty },
                            { label: 'Express Duty Amount', value: data.expressDutyAmount },
                            { label: 'Remark', value: data.remark }
                        ].map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box display="flex" alignItems="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                                        {item.label}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="500">
                                        {item.value || '-'}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Paper>
    );
};

const InquiryReview = ({ formData, onSubmit, onBack, onEditStep, loggedInUser, mode, selectedCompany, isInvoiceLocked, connectionString }) => {
    console.log(formData)
    const { client, product, consigner, transporter, export: exportData } = formData;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [openChangeDialog, setOpenChangeDialog] = useState(false);
    const [newSeriesNumber, setNewSeriesNumber] = useState('');
    const [changeDialogError, setChangeDialogError] = useState('');
    const [changeDialogLoading, setChangeDialogLoading] = useState(false);
    const [changedDocumentNumber, setChangedDocumentNumber] = useState(null);

    // Real submit action — includes the pre-submit document-number sync, so
    // Alt+S and the Submit button both go through the exact same logic.
    const handleSubmitClick = () => {
        if (changedDocumentNumber && formData && formData.client) {
            console.log('Updating document number from', formData.client.quotationNumber || formData.client.inquiryNumber, 'to', changedDocumentNumber);
            if (client.quotationNumber !== undefined) {
                formData.client.quotationNumber = changedDocumentNumber;
            }
            if (client.inquiryNumber !== undefined) {
                formData.client.inquiryNumber = changedDocumentNumber;
            }
            console.log('After update, formData.client:', formData.client);
        }
        onSubmit();
    };

    // ──────────────── Alt+S (Submit/Update) shortcut ───────────────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    // This is the wizard's final step — Alt+S here is what actually calls the
    // add/update API (PurcDispatchMaster's handleSubmit), not just step
    // navigation. Alt+C (Cancel) is handled once in the parent
    // (PurcDispatchMaster.jsx) so it works from every step, not duplicated here.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyS") return;
            e.preventDefault();
            if (!isInvoiceLocked) {
                handleSubmitClick();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    const parseDocumentNumber = (docNo) => {
        if (!docNo) return { prefix: '', series: '', suffix: '' };
        const parts = docNo.split('/');
        if (parts.length !== 3) return { prefix: '', series: '', suffix: '' };
        return {
            prefix: parts[0],
            series: parts[1],
            suffix: parts[2]
        };
    };

    const buildDocumentNumber = (prefix, series, suffix) => {
        return `${prefix}/${series}/${suffix}`;
    };

    const handleChangeNumber = () => {
        const currentDocNo = client?.quotationNumber;
        if (!currentDocNo) return;
        const parsed = parseDocumentNumber(currentDocNo);
        setNewSeriesNumber(parsed.series);
        setChangeDialogError('');
        setOpenChangeDialog(true);
    };

    // ✅ Validate series number: exactly 4 digits, no special characters
    const validateSeriesNumber = (series) => {
        const trimmed = series.trim();
        if (!/^\d+$/.test(trimmed)) {
            return { valid: false, error: 'Series number must contain only digits (0-9)' };
        }
        if (trimmed.length > 4) {
            return { valid: false, error: 'Series number must be exactly 4 digits maximum' };
        }
        const normalized = trimmed.padStart(4, '0');
        return { valid: true, normalized };
    };

    const handleConfirmChange = async () => {
        if (!newSeriesNumber.trim()) {
            setChangeDialogError('Series number cannot be empty');
            return;
        }

        const validation = validateSeriesNumber(newSeriesNumber);
        if (!validation.valid) {
            setChangeDialogError(validation.error);
            return;
        }

        const currentDocNo = client?.quotationNumber;
        const parsed = parseDocumentNumber(currentDocNo);
        const newDocumentNumber = buildDocumentNumber(parsed.prefix, validation.normalized, parsed.suffix);

        if (newDocumentNumber === currentDocNo) {
            setChangeDialogError('New number must be different from current');
            return;
        }

        setChangeDialogLoading(true);
        setChangeDialogError('');

        try {
            const companyId = selectedCompany?.CompId || selectedCompany?.companyId || selectedCompany?.id;

            if (!companyId) {
                console.error('Company ID not found. selectedCompany:', selectedCompany);
                setChangeDialogError('Company information not available. Please ensure company is selected.');
                setChangeDialogLoading(false);
                return;
            }

            console.log('Checking duplicate number:', { module: 'PurchaseChallan', numberValue: newDocumentNumber, companyId });

            const response = await axios.post(`${connectionString}/api/check-duplicate-number`, {
                module: 'PurchaseChallan',
                numberValue: newDocumentNumber,
                companyId: companyId
            });

            if (response.data?.exists) {
                setChangeDialogError('This number already exists');
            } else {
                client.quotationNumber = newDocumentNumber;
                setOpenChangeDialog(false);
                setNewSeriesNumber('');
            }
        } catch (error) {
            console.error('Error checking duplicate:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Error validating number';
            setChangeDialogError(errorMsg);
        } finally {
            setChangeDialogLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenChangeDialog(false);
        setNewSeriesNumber('');
        setChangeDialogError('');
    };

    const formatINR = (value) => {
        if (isNaN(value)) return "₹0.00";
        return value.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        });
    };

    const renderProductCards = () => {
        if (!product?.tableData?.length) {
            return (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                    No product data available.
                </Typography>
            );
        }

        return (
            <Box>
                {[...product.tableData].filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).reverse().map((row, index) => {
                    const {
                        productCode,
                        description,
                        unit,
                        tax,
                        qty,
                        rate,
                        disc,
                    } = row;

                    const amount = (qty * rate).toFixed(2);
                    const discAmount = (qty * rate * (1 - disc / 100)).toFixed(2);
                    const gstAmount = ((discAmount * tax) / 100).toFixed(2);

                    const isSameState = (formData?.client?.details?.State || '').toUpperCase() === (selectedCompany?.State || '').toUpperCase();

                    return (
                        <Box
                            key={index}
                            sx={{
                                border: "1px solid #cbd5e1",
                                borderRadius: 2,
                                p: 1.5,
                                mb: 1.5,
                                bgcolor: "#fff",
                            }}
                        >
                            {/* Line 1 */}
                            <Typography sx={{ fontWeight: 600 }}>
                                {productCode} —{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: "italic",
                                        fontWeight: "normal",
                                        display: "inline",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal"
                                    }}
                                >
                                    {description}
                                </Box>
                            </Typography>

                            {/* Qty */}
                            <Grid container justifyContent="space-between">
                                <Typography>Qty:</Typography>
                                <Typography>{qty}</Typography>
                            </Grid>

                            {/* Rate */}
                            <Grid container justifyContent="space-between">
                                <Typography>Rate:</Typography>
                                <Typography>₹{rate}</Typography>
                            </Grid>

                            {/* Discount */}
                            <Grid container justifyContent="space-between">
                                <Typography>Disc:</Typography>
                                <Typography>{disc}%</Typography>
                            </Grid>

                            {/* GST depending on state */}
                            <Grid container justifyContent="space-between">
                                <Typography>{isSameState ? "CGST+SGST:" : "IGST:"}</Typography>
                                <Typography>
                                    ₹
                                    {isSameState
                                        ? `${(gstAmount / 2).toFixed(2)} + ${(gstAmount / 2).toFixed(
                                            2
                                        )}`
                                        : gstAmount}
                                </Typography>
                            </Grid>

                            {/* Final Amount */}
                            <Grid container justifyContent="space-between">
                                <Typography>Amount:</Typography>
                                <Typography>₹{discAmount}</Typography>
                            </Grid>

                            {/* Optional Fields */}
                            {row.unit && (
                                <Grid container justifyContent="space-between">
                                    <Typography>Unit:</Typography>
                                    <Typography>{unit}</Typography>
                                </Grid>
                            )}

                            {row.remark && row.remark.trim() !== '' && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        mt: 1,
                                        display: 'block',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        whiteSpace: 'normal'
                                    }}
                                >
                                    Remark: {row.remark}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Box
                        sx={{
                            border: '1px solid #cbd5e1',
                            borderRadius: 1,
                            p: 1,
                            minWidth: 280,
                            backgroundColor: '#f9fafb',
                        }}
                    >
                        {(() => {
                            const s = product?.summary || {};
                            const items = [
                                { label: 'Basic Amount:', value: `₹${s.basic?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },]

                            if (s.igst > 0) {
                                items.push({
                                    label: 'IGST:',
                                    value: `₹${s.igst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                });
                            } else {
                                if (s.cgst > 0) {
                                    items.push({
                                        label: 'CGST:',
                                        value: `₹${s.cgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                    });
                                }
                                if (s.sgst > 0) {
                                    items.push({
                                        label: 'SGST:',
                                        value: `₹${s.sgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                    });
                                }
                            }

                            items.push({ label: 'Round Off:', value: `₹${s.roundOff?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }, { label: 'Grand Total:', value: `₹${s.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });


                            return items.map((item, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: '0.85rem' }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontSize: '0.85rem', textAlign: 'right' }}
                                    >
                                        {item.value}
                                    </Typography>
                                </Box>
                            ));
                        })()}
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>📋 Review Purchase Challan Summary</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Please review all details before submission. Use the back button to make changes if needed.
            </Typography>

            <Section title="Client Details" data={client} stepIndex={0} onEditStep={onEditStep} onChangeNumber={handleChangeNumber} changedDocumentNumber={changedDocumentNumber} />

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">Product Details</Typography>
                    {product?.tableData?.length > 0 && (
                        <Button size="small" variant="outlined" onClick={() => onEditStep(1)}>
                            Edit
                        </Button>
                    )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {isMobile ? (
                    renderProductCards()
                ) : (
                    <>
                        {product?.tableData?.length > 0 ? (
                            <Box>
                                <Box sx={{ width: '100%' }}>
                                    <DataGrid
                                        rows={[...product.tableData]
                                            .filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300)
                                            .reverse()
                                            .map((r, i) => ({ ...r, id: i + 1, index: i }))}
                                        columns={[
                                            { field: 'id', headerName: '#', width: 40, align: 'center', headerAlign: 'center' },
                                            {
                                                field: 'product', headerName: 'Product', flex: 1, minWidth: 160, sortable: false, filterable: false,
                                                renderCell: (params) => {
                                                    const { productCode, description, unit, tax, size, color, batchNo, expDate, serialNo, remark, dispatchQty, rate } = params.row;
                                                    const gstAmount = ((dispatchQty * rate * tax) / 100).toFixed(2);
                                                    const isSameState = formData?.client?.details?.State === selectedCompany?.state;
                                                    return (
                                                        <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column' }}>
                                                            <Typography variant="body2" fontWeight="bold" component="div">
                                                                <Box component="span" sx={{ fontSize: '0.85rem' }}>
                                                                    {productCode}
                                                                </Box>
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                                                {description}
                                                            </Typography>
                                                            <Box display='flex' flexDirection='row'>
                                                                <Typography variant="caption" display="block">Unit: {unit} | GST: {tax}%</Typography>
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    {isSameState
                                                                        ? `SGST: ₹${(gstAmount / 2).toFixed(2)} + CGST: ₹${(gstAmount / 2).toFixed(2)}`
                                                                        : `IGST: ₹${gstAmount}`}
                                                                </Typography>
                                                                {(size || color) && (
                                                                    <Typography variant="caption" display="block">
                                                                        {size && `Size: ${size}`} {color && `| Color: ${color}`}
                                                                    </Typography>
                                                                )}
                                                                {(batchNo || expDate || serialNo) && (
                                                                    <Typography variant="caption" display="block">
                                                                        {batchNo && `Batch: ${batchNo}`} {expDate && `| Exp: ${expDate}`} {serialNo && `| S/N: ${serialNo}`}
                                                                    </Typography>
                                                                )}
                                                                {remark && (
                                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                                        Remark: {remark}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                }
                                            },
                                            { field: 'dispatchQty', headerName: 'Qty', width: 60, align: 'center', headerAlign: 'center' },
                                            { field: 'rate', headerName: 'Rate', width: 110, align: 'right', headerAlign: 'center', renderCell: (params) => formatINR(Number(params.value) || 0) },
                                            {
                                                field: 'disc', width: 70, align: 'center', headerAlign: 'center',
                                                renderHeader: () => (
                                                    <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                                                        <Typography variant="body2">Disc</Typography>
                                                        <Typography variant="caption" color="text.secondary"> (%) </Typography>
                                                    </Box>
                                                )
                                            },
                                            {
                                                field: 'amount',
                                                headerName: 'Amount ₹',
                                                width: 110,
                                                align: 'right',
                                                headerAlign: 'right',
                                                renderCell: (params) => {
                                                    const qty = Number(params.row.dispatchQty) || 0;
                                                    const rate = Number(params.row.rate) || 0;
                                                    const disc = Number(params.row.disc) || 0;
                                                    const total = qty * rate * (1 - disc / 100);
                                                    return formatINR(total);
                                                },
                                            },
                                        ]}
                                        getRowHeight={() => 'auto'}
                                        autoHeight
                                        hideFooter
                                        disableRowSelectionOnClick
                                        sx={{
                                            mt: 1,
                                            border: '1px solid #cbd5e1',
                                            borderRadius: 1,
                                            '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                            '& .MuiDataGrid-columnHeader': {
                                                backgroundColor: '#f0f0f0',
                                                paddingTop: '4px',
                                                paddingBottom: '4px',
                                                height: 42,
                                                lineHeight: '1.2rem',
                                                fontSize: '13px',
                                            },
                                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#edf2feff' },
                                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                                        }}
                                    />
                                </Box>

                                <Box mt={2} display="flex" justifyContent="flex-end">
                                    <Box
                                        sx={{
                                            border: '1px solid #cbd5e1',
                                            borderRadius: 1,
                                            p: 1,
                                            minWidth: 280,
                                            backgroundColor: '#f9fafb',
                                        }}
                                    >
                                        {(() => {
                                            const s = product?.summary || {};
                                            const items = [
                                                { label: 'Basic Amount:', value: `₹${s.basic?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },]

                                            if (s.igst > 0) {
                                                items.push({
                                                    label: 'IGST:',
                                                    value: `₹${s.igst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                                });
                                            } else {
                                                if (s.cgst > 0) {
                                                    items.push({
                                                        label: 'CGST:',
                                                        value: `₹${s.cgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                                    });
                                                }
                                                if (s.sgst > 0) {
                                                    items.push({
                                                        label: 'SGST:',
                                                        value: `₹${s.sgst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                                                    });
                                                }
                                            }

                                            items.push({ label: 'Round Off:', value: `₹${s.roundOff?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }, { label: 'Grand Total:', value: `₹${s.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });


                                            return items.map((item, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{ fontSize: '0.85rem' }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        sx={{ fontSize: '0.85rem', textAlign: 'right' }}
                                                    >
                                                        {item.value}
                                                    </Typography>
                                                </Box>
                                            ));
                                        })()}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Typography color="text.secondary">No product data available.</Typography>
                        )}
                    </>)}

            </Paper>

            {/* <Section title="Consigner Details" data={consigner} stepIndex={2} onEditStep={onEditStep} />
            <Section title="Transporter Details" data={transporter} stepIndex={2} onEditStep={onEditStep} /> */}
            {/* <Section title="Export Details" data={exportData} stepIndex={4} onEditStep={onEditStep} /> */}

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Dispatch Details</Typography>
                    <Button size="small" variant="outlined" onClick={() => onEditStep(2)}>
                        Edit
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ================= CONSIGNER ================= */}
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                    {/* Ship From */}
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Ship From</Typography>

                        <Typography variant="body2"><b>Client:</b> {consigner?.shipFrom?.clientname}</Typography>
                        <Typography variant="body2"><b>Contact:</b> {consigner?.shipFrom?.ContactPerson}</Typography>
                        <Typography variant="body2"><b>Mobile:</b> {consigner?.shipFrom?.mobileNo}</Typography>
                        <Typography variant="body2"><b>Address:</b> {consigner?.shipFrom?.Address}</Typography>
                        <Typography variant="body2">
                            {consigner?.shipFrom?.City}
                            {consigner?.shipFrom?.State ? `, ${consigner.shipFrom.State}` : ""}
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                    {/* Ship To */}
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Ship To</Typography>

                        <Typography variant="body2"><b>Client:</b> {consigner?.shipTo?.clientname}</Typography>
                        <Typography variant="body2"><b>Contact:</b> {consigner?.shipTo?.ContactPerson}</Typography>
                        <Typography variant="body2"><b>Mobile:</b> {consigner?.shipTo?.mobileNo}</Typography>
                        <Typography variant="body2"><b>Address:</b> {consigner?.shipTo?.Address}</Typography>
                        <Typography variant="body2">
                            {consigner?.shipTo?.City}
                            {consigner?.shipTo?.State ? `, ${consigner?.shipTo?.State}` : ""}
                        </Typography>
                    </Box>
                </Box>

                {/* ================= TRANSPORTER ================= */}
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Transporter:</b> {transporter?.transporterName}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Mode:</b> {transporter?.transMode}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>GST No:</b> {transporter?.gstNo}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Vehicle:</b> {transporter?.vehicleNumber}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Vehicle Type:</b> {transporter?.vehicleType}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Total Pkg:</b> {transporter?.totalPkg}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Sub Type:</b> {transporter?.subType}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Dispatch From:</b> {transporter?.dispatchFrom}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>Dispatch To:</b> {transporter?.dispatchTo}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>LR No:</b> {transporter?.lrNumber}</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2"><b>LR Date:</b> {transporter?.lrDate}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Box mt={3} display="flex" justifyContent="space-between">
                <Button variant="outlined" onClick={onBack}>Back</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitClick}
                    disabled={isInvoiceLocked}
                >
                    {mode === 'edit' ? "UPDATE" : "SUBMIT"}
                </Button>
            </Box>

            <Dialog open={openChangeDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Change Document Number</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {changeDialogError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {changeDialogError}
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Current Number: <strong>{client?.quotationNumber}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Format: Prefix / Series Number / Suffix (you can only change the series number)
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Series Number"
                        value={newSeriesNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            // ✅ Only allow digits and max 4 characters
                            const filtered = value.replace(/\D/g, '').slice(0, 4);
                            setNewSeriesNumber(filtered);
                        }}
                        disabled={changeDialogLoading}
                        placeholder="e.g., 00002"
                        variant="outlined"
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={changeDialogLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmChange}
                        variant="contained"
                        disabled={changeDialogLoading}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        {changeDialogLoading && <CircularProgress size={20} />}
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InquiryReview;